import { Component, OnInit, Input, ElementRef, NgZone, OnDestroy } from '@angular/core';
import { ChartsService } from '../charts.service';
import { CurrencyPipe } from '@angular/common';
import { ProposalEventService } from 'app/services/proposal.service';
import { Chart, ChartComponentLike } from 'chart.js';
import { isInteger } from 'lodash';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { takeUntil, take } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { UserPreferences, UsersService } from 'app/main/pages/users/users.service';

@Component({
    selector: 'app-monthy-proposals-chart',
    templateUrl: './monthy-proposals-chart.component.html',
    styleUrls: ['./monthy-proposals-chart.component.scss']
})
export class MontlyProposalsChartComponent implements OnInit, OnDestroy {
    @Input('hashId') hashId?: string
    @Input('customerId') customerId?: string

    data = [];
    totalAproved: number = 0;
    userPreferences$: Observable<UserPreferences>;
    chart!: Chart;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private chartsService: ChartsService,
        private _proposalEventService : ProposalEventService,
        private elementRef: ElementRef,
        private ngZone: NgZone,
        private _usersService: UsersService,

    ) {
        this.userPreferences$ = this._usersService.userPreferences$;
    }

    ngOnDestroy(): void {
        if(this.chart){
            this.chart.destroy();
        }
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    ngOnInit() {
        Chart.register(ChartDataLabels);

        this._proposalEventService.proposal$.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
            this.populate();
        });

        this.userPreferences$.pipe(takeUntil(this._unsubscribeAll)).subscribe(() => {
            if (this.chart) {
                this.populate();
            }
        });
    }

    ngAfterViewInit(){
        this.initEmptyChart();
        this.populate();
    }

    initEmptyChart() {
        if(this.chart){
            this.chart.destroy();
        }

        let canvas = this.elementRef.nativeElement.querySelector(`#canvas-monthy`);
        this.chart = new Chart(canvas, {
            type: "bar",
            data: {
                datasets: this.data
            },
        });
    }

    populate() {
        let params = {};

        if(this.hashId) {
            params['hashId'] = this.hashId;
        }

        if(this.customerId) {
            params['customerId'] = this.customerId;
        }

        this.userPreferences$.pipe(take(1)).subscribe(userPreferences => {
            const hideValues = userPreferences.hide_dashboard_values;

            this.chartsService.getLastProposalsChart(params).subscribe(res => {
                this.createChart(res, hideValues);
            });
        });
    }

    private createChart(res: any, hideValues: boolean) {
        if(this.chart){
            this.chart.destroy();
        }

        let datasets = res.data.chart
        this.totalAproved = res.data.total
        let htmlRef = this.elementRef.nativeElement.querySelector(`#canvas-monthy`);
        this.data = res.data

        this.ngZone.runOutsideAngular(() => {
            this.chart = new Chart(htmlRef, {
                type: "bar",
                data: {
                    datasets: datasets
                },
                options: {
                    maintainAspectRatio: false,
                    responsive: true,
                    interaction: {
                        includeInvisible: false
                    },
                    scales: {
                        x: {
                            grid: {
                                display: false
                            },
                            stacked: true,
                        },
                        y: {
                            display: false,
                            stacked: true,
                            grace: '50%',
                            beginAtZero: true
                        },
                    },
                    locale: 'pt-BR',
                    plugins: {
                        legend: {
                            display: false,
                            position: 'bottom',
                        },
                        tooltip: {
                            enabled: !hideValues,
                            callbacks: {
                                label: function(context) {
                                    console.log("runoutsideangular hideValues", hideValues);
                                    let label = context.dataset.label || '';

                                    if (label) {
                                        label += ': ';
                                    }

                                    if (context.parsed !== null) {
                                        label += new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(context.parsed.y);
                                    }

                                    return label;
                                },
                            }
                        },
                        datalabels: {
                            display: !hideValues,
                            color: '#676767',
                            font: {
                                weight: 'bold'
                            },
                            anchor: 'end',
                            align: 'top',
                            formatter: (value, context) => {
                                const datasetArray = []
                                context.chart.data.datasets.forEach((dataset) => {
                                    if(dataset.data[context.dataIndex] != undefined){
                                        datasetArray.push(dataset.data[context.dataIndex]);
                                    }
                                });

                                function totalSum(total, datapoint){
                                    if(!datapoint) return total;
                                    return total + datapoint.y
                                }

                                let sum = datasetArray.reduce(totalSum, 0)

                                if(context.datasetIndex === datasetArray.length - 1){
                                    let total:any = sum / 1000;

                                    if(sum <= 999999){
                                        if(!isInteger(total)){
                                            total = total.toFixed(2)
                                        }
                                        total = total + 'K'
                                    } else {
                                        total = (total / 1000);
                                        if(!isInteger(total)){
                                            total = total.toFixed(2)
                                        }
                                        total = total + 'M';
                                    }

                                    return total;
                                }

                                return ''
                            },
                        },
                    }
                }
            });
        })
    }

    valueFormatting(value: number): string {
        if (!value) value = 0
        return (new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })).format(value).replace('R$', '')
    }
}
